// src/pages/BrandDetail/components/ReviewMetricsSection.js
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import './ReviewMetricsSection.css';

const ReviewMetricsSection = ({ reviewMetric }) => {
  const { translate } = useLanguage();

  // State for filtered data
  const [filteredDates, setFilteredDates] = useState([]);
  const [filteredRatings, setFilteredRatings] = useState([]);
  const [filteredCounts, setFilteredCounts] = useState([]);
  
  // Initialize data when reviewMetric changes
  useEffect(() => {
    if (!reviewMetric) return;
    
    // Initialize with all data initially
    const { dates, avg_rating, review_count } = reviewMetric;
    setFilteredDates(dates);
    setFilteredRatings(avg_rating);
    setFilteredCounts(review_count);
  }, [reviewMetric]);

  // Return early if no review metrics
  if (!reviewMetric) return null;

  const preprocessRatingData = (dates, ratings) => {
    // Create a copy of the filtered ratings
    const processedRatings = [...ratings];
    
    // Simple linear interpolation function
    const interpolate = (index) => {
      // Find the nearest non-null values before and after
      let before = index - 1;
      while (before >= 0 && (processedRatings[before] === null || processedRatings[before] === undefined)) {
        before--;
      }
      
      let after = index + 1;
      while (after < processedRatings.length && (processedRatings[after] === null || processedRatings[after] === undefined)) {
        after++;
      }
      
      // If we have values on both sides, interpolate
      if (before >= 0 && after < processedRatings.length) {
        const beforeVal = processedRatings[before];
        const afterVal = processedRatings[after];
        const range = after - before;
        const weight = (index - before) / range;
        return beforeVal + (afterVal - beforeVal) * weight;
      }
      
      // If we only have values on one side, use that
      if (before >= 0) return processedRatings[before];
      if (after < processedRatings.length) return processedRatings[after];
      
      // If we don't have values on either side, return null
      return null;
    };
    
    // Fill in missing values
    for (let i = 0; i < processedRatings.length; i++) {
      if (processedRatings[i] === null || processedRatings[i] === undefined || isNaN(processedRatings[i])) {
        processedRatings[i] = interpolate(i);
      }
    }
    
    return processedRatings;
  };

  // Use the preprocessed data for the chart
  const interpolatedRatings = preprocessRatingData(filteredDates, filteredRatings);

  // Convert data to recharts format
  const chartData = filteredDates.map((date, index) => {
    const d = new Date(date);
    const formattedDate = `${d.getFullYear().toString().slice(-2)}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    
    return {
      date: formattedDate,
      originalDate: date,
      reviewCount: filteredCounts[index],
      avgRating: interpolatedRatings[index]
    };
  });

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const date = new Date(data.originalDate);
      
      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: '600',
            color: '#334155',
            marginBottom: '4px'
          }}>
            {translate('brandDetailPage.reviewMetrics.tooltip.dateFormat', {
              year: date.getFullYear(),
              month: date.getMonth() + 1
            })}
          </p>
          {payload.map((entry) => (
            <p key={entry.dataKey} style={{
              margin: 0,
              fontSize: '13px',
              color: '#475569'
            }}>
              {entry.dataKey === 'avgRating' 
                ? translate('brandDetailPage.reviewMetrics.tooltip.avgRating', {
                    value: parseFloat(entry.value).toFixed(2)
                  })
                : translate('brandDetailPage.reviewMetrics.tooltip.reviewCount', {
                    value: entry.value.toLocaleString()
                  })
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Y-axis formatter for review count
  const formatYAxisRight = (value) => {
    return value >= 1000 ? (value/1000).toFixed(1) + 'k' : value;
  };

  // Y-axis formatter for rating
  const formatYAxisLeft = (value) => {
    return value.toFixed(1);
  };

  // Get trend text for display
  const getTrendText = (trend) => {
    if (trend === '상승' || trend === 'increasing') return 'Increasing';
    if (trend === '하락' || trend === 'decreasing') return 'Decreasing';
    return 'Stable';
  };

  return (
    <section className="glass-card review-metrics-section-new">
      {/* Header */}
      <div className="review-metrics-header-new">
        <div className="review-metrics-title-group">
          <h2 className="font-serif review-metrics-title">
            {translate('brandDetailPage.reviewMetrics.title') || 'Brand Review Trends'}
          </h2>
          <p className="review-metrics-subtitle">Historical view of customer sentiment and engagement</p>
        </div>

        {/* Trend Indicators */}
        <div className="trend-indicators">
          <div className="trend-indicator-card">
            <span className="trend-dot trend-dot-blue"></span>
            <div className="trend-indicator-content">
              <span className="trend-indicator-label">Rating Trend</span>
              <span className="trend-indicator-value">{getTrendText(reviewMetric.rating_trend)}</span>
            </div>
          </div>
          <div className="trend-indicator-card">
            <span className="trend-dot trend-dot-indigo"></span>
            <div className="trend-indicator-content">
              <span className="trend-indicator-label">Review Vol</span>
              <span className="trend-indicator-value">{getTrendText(reviewMetric.review_count_trend)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="review-chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              bottom: 20,
              left: 20,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(0, 0, 0, 0.03)" 
              horizontal={true}
              vertical={false}
            />
            <XAxis 
              dataKey="date" 
              axisLine={false}
              tickLine={false}
              tick={{ 
                fontSize: 12, 
                fontWeight: 500, 
                fill: '#64748b' 
              }}
              interval="preserveStartEnd"
              minTickGap={20}
            />
            <YAxis 
              yAxisId="left"
              orientation="left"
              domain={[3, 5.2]}
              axisLine={false}
              tickLine={false}
              tick={{ 
                fontSize: 12, 
                fontWeight: 500, 
                fill: '#64748b' 
              }}
              tickFormatter={formatYAxisLeft}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ 
                fontSize: 12, 
                fontWeight: 500, 
                fill: '#93c5fd' 
              }}
              tickFormatter={formatYAxisRight}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{
                paddingBottom: '20px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#334155'
              }}
            />
            <Bar
              yAxisId="right"
              dataKey="reviewCount"
              fill="rgba(165, 180, 252, 0.3)"
              stroke="rgba(165, 180, 252, 0.9)"
              strokeWidth={1}
              radius={[4, 4, 0, 0]}
              name={translate('brandDetailPage.reviewMetrics.labels.reviewCount') || 'Review Count'}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="avgRating"
              stroke="#3B82F6"
              strokeWidth={3}
              dot={false}
              activeDot={{
                r: 5,
                fill: '#ffffff',
                stroke: '#3B82F6',
                strokeWidth: 2
              }}
              name={translate('brandDetailPage.reviewMetrics.labels.avgRating') || 'Avg Rating'}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

export default ReviewMetricsSection;
